import { Injectable } from '@angular/core';
import { defaultAlerts } from '@app/translations';
import { Locale } from '@finxone-platform/shared/enums';
import { AlertHandlerService } from '@finxone-platform/shared/services';
import {
  AlertData,
  AlertResponse,
  AlertStatusFlag,
  AlertsType,
} from '@finxone-platform/shared/sys-config-types';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { catchError, map, of, tap } from 'rxjs';
import {
  AddProgressBarWorkflowStack,
  RemoveProgressBarWorkflowStack,
} from '../actions/progress-bar-workflow.action';
import {
  GetProjectAlerts,
  UpdateAlertBasedOnKey,
  UpdateIsDefault,
  UpdateProjectAlerts,
} from '../actions/project-alerts.action';
import { ApiService } from '../services/api-service/api.service';

export interface ProjectAlertsStateModel {
  toasterAlerts: AlertResponse;
}

@State<ProjectAlertsStateModel>({
  name: 'ProjectAlerts',
  defaults: {
    toasterAlerts: {} as AlertResponse,
  },
})
@Injectable()
export class ProjectAlertsState {
  constructor(private apiService: ApiService, private alertHandlerService: AlertHandlerService) {}

  @Selector()
  static getProjectAlertsState(state: ProjectAlertsStateModel) {
    return state;
  }

  @Selector()
  static getProjectToasterAlertsFromState(state: ProjectAlertsStateModel) {
    return state.toasterAlerts;
  }

  @Action(GetProjectAlerts)
  fetchProjectAlerts(ctx: StateContext<ProjectAlertsStateModel>, action: GetProjectAlerts) {
    if (action.projectId && action.params?.key) {
      ctx.dispatch(new AddProgressBarWorkflowStack({ uniqueId: 'GetProjectAlerts' }));

      return this.apiService
        .getCall<AlertResponse>(`/project-alerts/toaster/${action.projectId}`, {
          params: action.params,
        })
        .pipe(
          map((res) => {
            if (res) {
              if (Object.keys(res.value)?.length) {
                ctx.patchState({
                  ...ctx.getState(),
                  toasterAlerts: res,
                });
              } else {
                res.value = defaultAlerts[res.locale]?.alerts;
                ctx.patchState({
                  ...ctx.getState(),
                  toasterAlerts: res,
                });
              }
            } else {
              const alerts = defaultAlerts[Locale.en_GB]?.alerts;
              ctx.patchState({
                ...ctx.getState(),
                toasterAlerts: {
                  key: 'alerts',
                  locale: Locale.en_GB,
                  value: alerts,
                  isDefault: true,
                },
              });
            }
            ctx.dispatch(
              new RemoveProgressBarWorkflowStack({
                uniqueId: 'GetProjectAlerts',
              }),
            );
          }),
          catchError((err) => {
            console.error('Error fetching project alerts config', err);
            ctx.dispatch(
              new RemoveProgressBarWorkflowStack({
                uniqueId: 'GetProjectAlerts',
              }),
            );
            throw err;
          }),
        )
        .subscribe();
    } else {
      return of(null);
    }
  }

  @Action(UpdateProjectAlerts)
  updateProjectAlerts(ctx: StateContext<ProjectAlertsStateModel>, action: UpdateProjectAlerts) {
    if (action.projectId && action.payload?.key) {
      ctx.dispatch(new AddProgressBarWorkflowStack({ uniqueId: 'UpdateProjectAlerts' }));
      return this.apiService
        .postCall<AlertsType>(`/project-alerts/toaster/${action.projectId}`, action.payload)
        .pipe(
          tap((response) => {
            if (response) {
              this.alertHandlerService.showAlertFn(
                'success',
                'Changes have been saved successfully',
                'Changes Saved!',
              );
            }
            ctx.dispatch(
              new RemoveProgressBarWorkflowStack({
                uniqueId: 'UpdateProjectAlerts',
              }),
            );
          }),
          catchError((err) => {
            console.error('Error submitting project alerts config', err);
            ctx.dispatch(
              new RemoveProgressBarWorkflowStack({
                uniqueId: 'UpdateProjectAlerts',
              }),
            );
            throw err;
          }),
        )
        .subscribe();
    } else {
      return of(null);
    }
  }

  @Action(UpdateAlertBasedOnKey)
  updateAlertBasedOnKey(ctx: StateContext<ProjectAlertsStateModel>, action: { alertData: AlertData }) {
    const state = ctx.getState();
    const keyToUpdate = Object.keys(state.toasterAlerts.value).find((key) => key === action.alertData.key);

    if (keyToUpdate) {
      const updatedProjectAlerts = {
        ...state.toasterAlerts,
        value: {
          ...state.toasterAlerts.value,
          [keyToUpdate]: {
            ...state.toasterAlerts.value[keyToUpdate],
            description: action.alertData.value.description,
            title: action.alertData.value.title,
            status: action.alertData.value?.status as AlertStatusFlag,
            viewType: action.alertData.value?.viewType,
            viewRedirect: action.alertData.value?.viewRedirect,
            persona: action.alertData.value?.persona,
            type: action.alertData.value?.type,
          },
        },
      };
      ctx.patchState({
        ...ctx.getState(),
        toasterAlerts: updatedProjectAlerts,
      });
    } else {
      console.log('No matching key found');
    }
  }

  @Action(UpdateIsDefault)
  updateIsDefault(ctx: StateContext<ProjectAlertsStateModel>, action: { isDefault: boolean }) {
    const state = ctx.getState();

    ctx.patchState({
      ...ctx.getState(),
      toasterAlerts: {
        ...state.toasterAlerts,
        isDefault: action.isDefault,
      },
    });
  }
}
